/**
 * apiFetch — shared fetch wrapper with auth header injection and 401 handling.
 *
 * ⚠ IT DOES NOT THROW ON 4xx/5xx. Only a 401 (and a genuine network failure)
 * throws; every other status is handed back as an ordinary Response. THE CALLER
 * MUST CHECK `res.ok`.
 *
 * That contract is easy to get wrong in the direction that hurts, because
 *
 *     try { await apiFetch(url, { method: 'POST' }); markSucceeded(); }
 *     catch { markFailed(); }
 *
 * LOOKS like it handles errors and does not: the catch only fires on a network
 * failure, so a 403/409/500 runs the success path. Every occurrence found on
 * 2026-09-24 was reporting success on a failed write — a refund request the
 * customer was told had been filed, a user revoke the admin was told had taken
 * effect, a permissions reset that left overrides in place.
 *
 * Use {@link apiFetchOk} for any call whose response you do not inspect. It
 * throws {@link ApiError} with the server's own message, so the `catch` those
 * call sites already have starts working as written.
 */
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('veripass_token');
  const { skipAuth = false, rawUnauthorized = false, ...fetchOptions } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers || {}),
  };
  if (!skipAuth && token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = path.startsWith('http') ? path : `${API_URL}${path}`;
  const response = await fetch(url, { ...fetchOptions, headers });

  // ⚠ A 401 MEANS TWO DIFFERENT THINGS, AND THIS USED TO CONFLATE THEM.
  //
  // On an authenticated request it means the session ended. On the LOGIN
  // request it means the email and password did not match — there was never a
  // session to expire. Because this block caught every 401, a user who simply
  // mistyped their password was told "Session expired", which is both wrong and
  // alarming: it suggests their account did something rather than that they made
  // a typo. AuthContext already reads the server's real message
  // ("Invalid email or password") and LoginPage already falls back to the
  // localised auth.invalidCredentials — neither was ever reached, because this
  // threw first.
  //
  // `rawUnauthorized` opts a call out: the 401 is handed back as an ordinary
  // response for the caller to interpret. Used by the login request. It does NOT
  // clear storage either, which matters — a failed login attempt should not sign
  // out a session the user already has in another tab.
  if (response.status === 401 && !rawUnauthorized) {
    // Token expired or invalid — clear and redirect to login
    localStorage.removeItem('veripass_token');
    localStorage.removeItem('veripass_user');

    // Only redirect if not already on login page (prevent infinite loop)
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
    throw new Error('Session expired');
  }

  return response;
}

/**
 * ApiError — a non-2xx response, as an exception.
 *
 * Carries the status and the server's parsed detail so a caller's existing
 * `catch (e) { setError(e.message) }` shows the real reason instead of a
 * generic string.
 */
export class ApiError extends Error {
  constructor(status, detail, body) {
    super(detail || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

/**
 * apiFetchOk — apiFetch that THROWS {@link ApiError} on any non-2xx.
 *
 * For calls whose response you do not otherwise read. Prefer it over a bare
 * `await apiFetch(...)`: with apiFetch, ignoring the return value silently
 * ignores the failure too.
 *
 * Where a call site needs the failure inline (to render a field error, say),
 * keep using apiFetch and check `res.ok` — that is still correct, just explicit.
 */
export async function apiFetchOk(path, options = {}) {
  const res = await apiFetch(path, options);
  if (!res.ok) {
    let body = {};
    try { body = await res.clone().json(); } catch { /* not JSON */ }
    const detail = body?.detail || body?.error || body?.message;
    throw new ApiError(res.status, detail, body);
  }
  return res;
}

/**
 * getPaymentConfig — public PortOne config for the checkout UI (Phase 1.4).
 * Returns { card_enabled, store_id, channel_key, report_price_krw }. `card_enabled`
 * is true only when the backend feature flag is on AND the keys are configured;
 * on any error it returns a safe default so the checkout UI degrades to
 * wire-transfer only / hides the single-report purchase card.
 * Unauthenticated (skipAuth).
 */
export async function getPaymentConfig() {
  const fallback = { card_enabled: false, store_id: '', channel_key: '', report_price_krw: 0 };
  try {
    const res = await apiFetch('/api/v1/public/payment-config', { skipAuth: true });
    if (!res.ok) return fallback;
    return await res.json();
  } catch {
    return fallback;
  }
}

export { API_URL };
